import { Link } from 'react-router'

import { Skeleton } from '@/components/ui/skeleton'
import type { EmailThread } from '@/models/internalEmail'
import { EmailThreadMessage } from './EmailThreadMessage'

/** Related records the internal app has a page for; anything else is unlinked. */
const RELATED_ROUTES: Record<string, string> = {
  SALES_LEAD: '/insure/internal/sales-leads',
  APPLICATION: '/insure/internal/applications',
}

const LINK_CLASS = 'text-blue-600 underline-offset-2 hover:underline'

function RelatedLink({
  relatedModel,
  relatedModelId,
}: {
  relatedModel: string | null
  relatedModelId: string | null
}) {
  const base = relatedModel ? RELATED_ROUTES[relatedModel] : undefined
  if (!base || !relatedModelId) return null
  return (
    <Link to={`${base}/${relatedModelId}`} className={LINK_CLASS}>
      {relatedModel === 'SALES_LEAD' ? 'Sales lead' : 'Application'}
    </Link>
  )
}

/**
 * One thread, read-only. Presentational: the caller fetches, because the COI
 * page's Emails tab reads the same thread shape from an org-scoped endpoint.
 */
export function EmailThreadView({
  threadKey,
  thread,
  isLoading,
}: {
  threadKey: string | null
  thread: EmailThread | undefined
  isLoading: boolean
}) {
  if (!threadKey) {
    return (
      <div className="text-muted-foreground flex h-full items-center justify-center rounded-lg border text-sm">
        Select a thread to read it.
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex h-full flex-col gap-3 overflow-hidden rounded-lg border p-4">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  if (!thread) {
    return (
      <div className="text-muted-foreground flex h-full items-center justify-center rounded-lg border p-6 text-center text-sm">
        That thread is no longer here — it may have been deleted since the link was shared.
      </div>
    )
  }

  const last = thread.messages[thread.messages.length - 1]

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border">
      <header className="bg-card border-b px-4 py-3">
        <h2 className="text-lg font-semibold wrap-break-word">{thread.subject || '(no subject)'}</h2>
        <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
          <span>
            {thread.messageCount} message{thread.messageCount === 1 ? '' : 's'}
          </span>
          {thread.org && (
            <Link to={`/insure/internal/customers/${thread.org.id}`} className={LINK_CLASS}>
              {thread.org.name}
            </Link>
          )}
          <RelatedLink relatedModel={last.relatedModel} relatedModelId={last.relatedModelId} />
        </div>
      </header>

      <div className="bg-muted/30 min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
        {thread.messages.map((message, index) => (
          <EmailThreadMessage
            key={message.id}
            message={message}
            // Gmail behaviour: the newest message is open, older ones collapsed.
            defaultExpanded={index === thread.messages.length - 1}
          />
        ))}
      </div>
    </div>
  )
}
